/**
 * surface_response — what a blaster bolt does to the thing it hits.
 *
 * A blaster bolt is fiction, so none of this is physics; it is the set of rules
 * that make a hit read as landing on a MATERIAL rather than on "a surface".
 * Metal does not owe you a welding shower, and a red bolt does not have to
 * throw red dust — the envelope can keep the weapon's colour while the debris
 * belongs to whatever was struck.
 *
 * WHERE THE MATERIAL COMES FROM. Not the display material's colour or UUID —
 * that breaks the moment an asset is re-textured or an LOD swaps in. Atlas
 * already knows two authoritative things about any point it can shoot:
 * PhysicsModule's collider class (`terrain` / `structure` / `other`) and, for
 * ground, the seeded BIOME at that world position. A shot into dune_desert
 * throws sand; the same shot into rocky_highlands chips stone. That mapping is
 * stable across re-cooks because it is derived from the world seed, not the art.
 */

/** The impact layers, each with its own lifetime — see the timed stack below. */
export interface ImpactProfile {
  /** Label for diagnostics and the hit feed. */
  material: string;
  /** Contact flash: radius (m) and life (ms). The "it arrived HERE" cue. */
  flash: { size: number; ms: number; color: number };
  /** Short-lived light so the receiver is actually LIT — bloom does not illuminate. */
  light: { color: number; intensity: number; rangeM: number; ms: number } | null;
  /** Fast hot ejecta: count and speed (m/s). Direction comes from the surface frame. */
  sparks: { count: number; speed: number; ms: number; color: number };
  /** Slower, heavier fragments that carry a sense of mass. */
  chips: { count: number; speed: number; ms: number; color: number };
  /** Dust or smoke: radius (m) and life (ms). Alpha-blended, never additive. */
  dust: { size: number; ms: number; color: number; opacity: number } | null;
  /** The mark left behind. */
  decal: 'scorch' | 'hole' | 'none';
  /** Sound key and gain. */
  sfx: { key: string; gain: number };
}

const scorchDark = 0x140f0c;

/** Base profiles by material. Numbers are starting values, tuned by eye, not measured. */
const PROFILES: Record<string, ImpactProfile> = {
  // Worked metal: a tight bright contact and a directional spray, ringing.
  metal: {
    material: 'metal',
    flash: { size: 0.30, ms: 34, color: 0xfff0d0 },
    light: { color: 0xffd9a0, intensity: 5.5, rangeM: 6, ms: 70 },
    sparks: { count: 22, speed: 7.5, ms: 260, color: 0xffc46a },
    chips: { count: 4, speed: 3.0, ms: 620, color: 0x9aa0aa },
    dust: { size: 0.35, ms: 520, color: 0x6a6a70, opacity: 0.30 },
    decal: 'scorch',
    sfx: { key: 'sfx_impact_metal', gain: 0.32 },
  },
  // Stone and concrete: pale flash, angular chips, a dry crack and grit.
  stone: {
    material: 'stone',
    flash: { size: 0.26, ms: 30, color: 0xffe6c8 },
    light: { color: 0xffca90, intensity: 3.8, rangeM: 5, ms: 60 },
    sparks: { count: 8, speed: 4.5, ms: 200, color: 0xffb070 },
    chips: { count: 10, speed: 3.6, ms: 900, color: 0x8d8577 },
    dust: { size: 0.75, ms: 1400, color: 0xa79e8c, opacity: 0.50 },
    decal: 'scorch',
    sfx: { key: 'sfx_impact_stone', gain: 0.30 },
  },
  // Sand and loose soil: the flash is half-buried in its own ejecta.
  sand: {
    material: 'sand',
    flash: { size: 0.18, ms: 22, color: 0xffe0a8 },
    light: { color: 0xffbe78, intensity: 2.2, rangeM: 4, ms: 45 },
    sparks: { count: 3, speed: 3.0, ms: 150, color: 0xffcf90 },
    chips: { count: 14, speed: 3.2, ms: 800, color: 0xc2a878 },
    dust: { size: 1.05, ms: 1700, color: 0xc9b888, opacity: 0.62 },
    decal: 'none',
    sfx: { key: 'sfx_impact_soil', gain: 0.26 },
  },
  // Soil, moss, spore mat: a dull thump and a low dark puff.
  soil: {
    material: 'soil',
    flash: { size: 0.17, ms: 22, color: 0xffd9a0 },
    light: { color: 0xffb070, intensity: 2.0, rangeM: 4, ms: 45 },
    sparks: { count: 3, speed: 2.6, ms: 150, color: 0xffc080 },
    chips: { count: 10, speed: 2.8, ms: 760, color: 0x6e5c42 },
    dust: { size: 0.85, ms: 1500, color: 0x7d6b50, opacity: 0.55 },
    decal: 'scorch',
    sfx: { key: 'sfx_impact_soil', gain: 0.26 },
  },
  // Crystal and glassy growth: a bright small contact and a shower of shards.
  crystal: {
    material: 'crystal',
    flash: { size: 0.32, ms: 40, color: 0xd8f4ff },
    light: { color: 0x9fdcff, intensity: 6.5, rangeM: 7, ms: 85 },
    sparks: { count: 18, speed: 6.5, ms: 300, color: 0xbfeaff },
    chips: { count: 12, speed: 4.2, ms: 950, color: 0xa8d8ea },
    dust: { size: 0.35, ms: 600, color: 0xb8d8e8, opacity: 0.25 },
    decal: 'scorch',
    sfx: { key: 'sfx_impact_crystal', gain: 0.30 },
  },
  // Flesh and armour plate: the response belongs to the target, not the world.
  body: {
    material: 'body',
    flash: { size: 0.16, ms: 26, color: 0xffb0a0 },
    light: { color: 0xff8060, intensity: 2.0, rangeM: 3, ms: 45 },
    sparks: { count: 5, speed: 3.5, ms: 180, color: 0xff9060 },
    chips: { count: 0, speed: 0, ms: 0, color: 0 },
    dust: { size: 0.25, ms: 420, color: 0x50302a, opacity: 0.28 },
    decal: 'none',
    sfx: { key: 'sfx_impact_body', gain: 0.34 },
  },
  // Anything unclassified: readable, restrained, never wrong-looking.
  generic: {
    material: 'generic',
    flash: { size: 0.22, ms: 28, color: 0xffe0b0 },
    light: { color: 0xffc080, intensity: 3.0, rangeM: 5, ms: 55 },
    sparks: { count: 10, speed: 5.0, ms: 220, color: 0xffc080 },
    chips: { count: 5, speed: 3.0, ms: 700, color: 0x8a8a8a },
    dust: { size: 0.45, ms: 800, color: 0x8a8278, opacity: 0.35 },
    decal: 'scorch',
    sfx: { key: 'sfx_impact_energy', gain: 0.28 },
  },
};

/** Ground biome → material. The seed decides this, so it survives a re-cook. */
const BIOME_MATERIAL: Record<string, string> = {
  dune_desert: 'sand',
  rocky_highlands: 'stone',
  crystal_forest: 'crystal',
  regolith_plains: 'soil',
  moss_wetland: 'soil',
  fungal_basin: 'soil',
};

/**
 * Resolve the impact profile for a hit.
 *
 * `kind` is PhysicsModule's collider class ('terrain' | 'structure' | 'other')
 * or 'ground' from the analytic fallback; `biome` is the seeded biome key at
 * the hit, used only for ground. `onBody` short-circuits to the character
 * response — a shot that reaches flesh should not also chip the wall behind it.
 */
export function impactProfile(kind: string | null, biome?: string | null, onBody = false): ImpactProfile {
  if (onBody) return PROFILES.body;
  if (kind === 'structure') return PROFILES.metal;
  if (kind === 'terrain' || kind === 'ground') {
    return PROFILES[BIOME_MATERIAL[biome ?? ''] ?? 'soil'] ?? PROFILES.generic;
  }
  return PROFILES.generic;
}

/** Every profile, for the harness and the debug menu. */
export function allProfiles(): ImpactProfile[] { return Object.values(PROFILES); }

/**
 * Scale an impact by how square-on it was and by the weapon's punch.
 *
 * A graze should not throw the same plume as a shot straight into the wall,
 * and a heavy weapon should. Counts are floored at 1 where the base had any, so
 * a glancing hit still reads as a hit rather than vanishing.
 */
export function scaleProfile(p: ImpactProfile, incidence: number, punch = 1): ImpactProfile {
  const k = (0.45 + 0.55 * Math.min(1, Math.max(0, incidence))) * Math.max(0.1, punch);
  const n = (base: number): number => (base <= 0 ? 0 : Math.max(1, Math.round(base * k)));
  return {
    ...p,
    flash: { ...p.flash, size: p.flash.size * k },
    light: p.light ? { ...p.light, intensity: p.light.intensity * k, rangeM: p.light.rangeM * (0.7 + 0.3 * k) } : null,
    sparks: { ...p.sparks, count: n(p.sparks.count), speed: p.sparks.speed * (0.8 + 0.2 * k) },
    chips: { ...p.chips, count: n(p.chips.count) },
    dust: p.dust ? { ...p.dust, size: p.dust.size * k } : null,
  };
}

export { scorchDark };
